// Session sync helper — creates or updates the backend wizard session that
// mirrors the local store. Used by the floor-plan export to persist the
// configuration as a remarketing lead (with explicit consent, per RGPD).
// CartPage keeps its own inline copy of this flow (security-audited path).

import type { WizardType, DivisionConfig, Selections, DeviceCombo } from '../store/wizardStore';

export function tokenHeader(token: string | null): Record<string, string> {
  return token ? { 'X-Session-Token': token } : {};
}

export interface SessionSyncInput {
  sessionId: string | null;
  accessToken: string | null;
  wizardType: WizardType | null;
  divisions: DivisionConfig[];
  selections: Selections;
  devices: Record<string, DeviceCombo[]>;
}

export interface SessionSyncResult {
  sessionId: string;
  accessToken: string | null;
}

export async function syncSession(input: SessionSyncInput): Promise<SessionSyncResult> {
  const payload = {
    wizardType: input.wizardType,
    divisions: input.divisions,
    selections: input.selections,
    devices: input.devices,
  };

  if (!input.sessionId) {
    const res = await fetch('/api/sessions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const json = (await res.json()) as {
      success: boolean;
      data?: { id: string; accessToken?: string };
    };
    if (!json.success || !json.data) throw new Error('create-session');
    return { sessionId: json.data.id, accessToken: json.data.accessToken ?? null };
  }

  const res = await fetch(`/api/sessions/${input.sessionId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...tokenHeader(input.accessToken) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('update-session');
  return { sessionId: input.sessionId, accessToken: input.accessToken };
}
